import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Undo2,
  Clock,
  CheckCircle2,
  Send,
  Truck,
  DollarSign,
  Calendar,
  FileText,
  AlertTriangle,
  Eye,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  shipped: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400',
  received: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
};

const creditColors = {
  pending: 'bg-amber-100 text-amber-700',
  issued: 'bg-emerald-100 text-emerald-700',
  applied: 'bg-blue-100 text-blue-700'
};

export const SupplierReturns = () => {const { t } = useTranslation("procurement");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/procurement/supplier-returns`;
      if (activeTab !== 'all') {
        url += `?status=${activeTab}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch supplier returns');
      const data = await response.json();
      setReturns(data);
    } catch (err) {
      toast.error('Failed to load supplier returns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReturns();
  }, [activeTab]);

  const filteredReturns = returns.filter((ret) => {
    if (!searchTerm) return true;
    return ret.return_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ret.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ret.po_number?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: returns.length,
    pending: returns.filter((r) => r.status === 'pending').length,
    approved: returns.filter((r) => r.status === 'approved').length,
    shipped: returns.filter((r) => r.status === 'shipped').length,
    totalValue: returns.reduce((sum, r) => sum + (r.total_value || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="procurement-supplier-returns">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("supplierReturns", "Supplier Returns")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageReturnsTo", "Manage returns to suppliers and credit notes")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReturns} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-orange-600 hover:bg-orange-700">
            <Plus className="h-4 w-4" />{t("newReturn", "New Return")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-5">
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Undo2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalReturns", "Total Returns")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("pending", "Pending")}</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <CheckCircle2 className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("approved", "Approved")}</p>
                <p className="text-2xl font-bold">{stats.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Truck className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("shipped", "Shipped")}</p>
                <p className="text-2xl font-bold">{stats.shipped}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalValue", "Total Value")}</p>
                <p className="text-2xl font-bold">${(stats.totalValue / 1000).toFixed(1)}k</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
                <TabsTrigger value="pending">{t("pending", "Pending")}</TabsTrigger>
                <TabsTrigger value="approved">{t("approved", "Approved")}</TabsTrigger>
                <TabsTrigger value="shipped">{t("shipped", "Shipped")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search returns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("return", "Return #")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("poReference", "PO Reference")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("vendor", "Vendor")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("reason", "Reason")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("items", "Items")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("value", "Value")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("credit", "Credit")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredReturns.map((ret) =>
                <tr key={ret.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4">
                      <span className="font-mono text-sm font-medium">{ret.return_number}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-orange-600">{ret.po_number}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm">{ret.vendor_name}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-1.5 text-sm">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                        {ret.reason}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="font-medium">{ret.total_items}</span>
                    </td>
                    <td className="py-4 px-4 text-right font-mono">
                      ${ret.total_value?.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <Badge className={creditColors[ret.credit_status] || creditColors.pending}>
                        {ret.credit_status}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <Badge className={statusColors[ret.status] || statusColors.pending}>
                        {ret.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost">
                          <Eye className="h-4 w-4" />
                        </Button>
                        {ret.status === 'approved' &&
                      <Button size="sm" variant="outline" className="gap-1">
                            <Truck className="h-3.5 w-3.5" />{t("ship", "Ship")}

                      </Button>
                      }
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredReturns.length === 0 &&
          <div className="py-12 text-center">
              <Undo2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noReturnsFound", "No returns found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};